import { BlockHead, ExpandTable, Selo, Explica } from "../../components/primitives";

// ─────────────────────────────────────────────────────────────
// Dados do Guia
// ─────────────────────────────────────────────────────────────

const CONCEITOS = [
  {
    termo: "LAB",
    icone: "🧪",
    def: "Ambiente de testes isolado. Toda nova configuração começa no LAB antes de ir para produção. O LAB usa instâncias e projetos Supabase separados para não interferir com clientes ativos.",
  },
  {
    termo: "Master",
    icone: "⚙",
    def: "Workflow n8n principal já validado e estável. É a versão de referência de onde novos workflows de cliente são derivados. Não deve ser editado diretamente.",
  },
  {
    termo: "Template",
    icone: "📋",
    def: "Versão do workflow preparada para ser clonada para um novo cliente. Contém variáveis substituíveis (convênios, unidades, mensagens). Cada template tem uma versão (ex: fluxomed-v1, fluxomed-v1.1).",
  },
  {
    termo: "Cliente",
    icone: "👤",
    def: "Configuração específica de um médico ou clínica que usa o FluxoMed. Cada cliente tem sua própria instância Evolution, projeto Supabase, convênios, unidades e mensagens personalizadas.",
  },
  {
    termo: "Instância Evolution",
    icone: "◉",
    def: "Identificador da conexão WhatsApp no Evolution API. Cada cliente tem sua própria instância, que representa o número de WhatsApp do consultório. Ex: 'dr-victor', 'fluxomed-sandbox'.",
  },
  {
    termo: "Projeto Supabase",
    icone: "🗄",
    def: "ID do projeto Supabase que armazena as sessões ativas do bot (tabela chatbot_sessions). Diferente do Supabase do CRM — o bot usa seu próprio banco operacional isolado.",
  },
  {
    termo: "Homologação",
    icone: "✅",
    def: "Processo de validação antes de ir para produção. Inclui testar o fluxo completo no LAB, validar convênios, unidades, mensagens e confirmar com o cliente que o bot está correto.",
  },
  {
    termo: "Follow-up",
    icone: "🔔",
    def: "Mensagem automática enviada quando o paciente inicia o atendimento mas não conclui. Dispara após um período configurável (padrão: 4h) dentro de uma janela horária (padrão: 8h–20h).",
  },
];

const FLUXO_STEPS = [
  {
    step: "LAB",
    icone: "🧪",
    desc: "Testar e validar o workflow no ambiente isolado. Simular todos os estados: menu, convênio, unidade, documentos, follow-up.",
  },
  {
    step: "Master",
    icone: "⚙",
    desc: "Atualizar o workflow Master se houve melhorias validadas no LAB. O Master é a referência sempre estável.",
  },
  {
    step: "Template",
    icone: "📋",
    desc: "Criar um Template a partir do Master para o novo cliente. Substituir variáveis: convênios, unidades, mensagens, instância.",
  },
  {
    step: "Cliente",
    icone: "👤",
    desc: "Importar o Template no n8n de produção. Ativar a instância Evolution. Homologar com o cliente. Marcar como Produção no CRM.",
  },
];

const VERSOES = [
  {
    versao: "v1.0",
    data: "2026-06-09",
    fase: "Fase 1",
    cor: "var(--info-ink)",
    bg: "var(--info-soft)",
    funcionalidades: [
      "Cadastro de clientes FluxoMed",
      "Convênios aceitos e recusados (por item separado)",
      "Unidades com endereço, dia, horário e flag quinzenal",
      "Templates de mensagem com pré-preenchimento automático",
      "Configuração de follow-up (delay, janela, timeouts)",
      "Checklist de homologação com progresso visual",
      "Exportação de config-{slug}.json compatível com n8n",
    ],
    impacto: "Fundação do módulo. O RC2Clicks CRM passa a ser a fonte única de configuração dos clientes FluxoMed.",
  },
  {
    versao: "v1.1",
    data: "2026-06-09",
    fase: "Fase 2",
    cor: "var(--success-ink)",
    bg: "var(--success-soft)",
    funcionalidades: [
      "Dashboard com visão geral e KPIs por status",
      "Pipeline de homologação com checklist visual por cliente",
      "Histórico de eventos por cliente (criação, atualização, exportação, homologação)",
      "Guia do Produto com documentação interna centralizada",
      "Seção Versões e Evolução — atualizada a cada fase",
    ],
    impacto: "Produto passa a ter gestão visual completa. Homologação rastreada. Documentação e histórico de eventos centralizados.",
  },
  {
    versao: "v1.3",
    data: "2026-06-10",
    fase: "Fase 4",
    cor: "var(--cat-teal-ink)",
    bg: "var(--cat-teal-soft)",
    funcionalidades: [
      "Central Operacional com pipeline visual por estágio (Rascunho → Produção)",
      "Indicador de saúde por cliente (semáforo 🔴🟡🟢) com contagem de pendências",
      "Gate de produção: bloqueia entrada sem homologação aprovada e implantação concluída",
      "Painel de pendências contextual na aba Geral (configuração, homologação, implantação)",
      "Dois novos estágios de pipeline: Implantação e Pronto p/ Produção",
      "Botão de avanço rápido no kanban (com validação automática de elegibilidade)",
      "Eventos de pipeline no histórico (pipeline_avancado, cliente_producao, cliente_pausado)",
      "Arquitetura escalável: padrão reutilizável para FluxoPet, FluxoEstética e demais produtos",
    ],
    impacto: "RC2Clicks passa a ter controle operacional centralizado de todos os clientes FluxoMed, com visibilidade completa do pipeline e gate automático antes de qualquer go-live.",
  },
  {
    versao: "v1.2",
    data: "2026-06-09",
    fase: "Fase 3",
    cor: "var(--cat-violet-ink)",
    bg: "var(--cat-violet-soft)",
    funcionalidades: [
      "Checklist de implantação operacional com 8 etapas, responsável e data por item",
      "Status de implantação por cliente (em andamento, concluída, pausada)",
      "Geração de README-{slug}.md com dados completos do cliente",
      "Exportação de checklist de implantação em markdown",
      "Exportação de checklist de homologação em markdown",
      "Linha do tempo por cliente com filtro por tipo de evento",
      "Registro automático de eventos de implantação no histórico",
    ],
    impacto: "O CRM passa a guiar todo o processo de implantação, do workflow ao cliente treinado, com rastreabilidade completa.",
  },
];

// ─────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────
const primeiraFrase = (t) => (t.match(/^.*?[.!?](\s|$)/) || [t])[0].trim();
const ordemVersao = (v) => v.versao.replace(/[^0-9.]/g, "").split(".").map(Number);

export function FluxoMedGuia() {
  const versoes = [...VERSOES].sort((a, b) => {
    const x = ordemVersao(a), y = ordemVersao(b);
    return (y[0] - x[0]) || ((y[1] || 0) - (x[1] || 0));
  });

  return (
    <div className="view" style={{ paddingTop: 0, maxWidth: 960 }}>
      <BlockHead title="O que é o FluxoMed" sub="Documentação interna: como o produto funciona e como evoluiu." />
      <Explica title="Bot de pré-agendamento no WhatsApp para médicos e clínicas (n8n + Evolution API + Supabase)">
        <p style={{ margin: 0 }}>
          Ele recebe o paciente, coleta o nome, valida o convênio, apresenta as unidades de
          atendimento disponíveis, coleta documentos e encaminha para a secretária confirmar
          a consulta. Todo o estado da conversa é armazenado no Supabase.
        </p>
        <p style={{ margin: "8px 0 0" }}>
          O bot é construído em <strong>n8n</strong> com <strong>Evolution API</strong> como gateway
          WhatsApp e <strong>Supabase</strong> como banco de sessões. Cada cliente tem sua própria
          instância isolada.
        </p>
      </Explica>

      <BlockHead title="Fluxo oficial de implantação" sub="Nada vai para produção sem passar pelo LAB. O CRM é a fonte de configuração; o n8n executa." />
      <div className="klist">
        {FLUXO_STEPS.map((st, i) => (
          <div key={st.step} className="klist-row" style={{ alignItems: "flex-start", paddingTop: 12, paddingBottom: 12 }}>
            <span className="ico info" style={{ width: 28, height: 28, borderRadius: 9, fontSize: 13 }} aria-hidden="true">{i + 1}</span>
            <span className="txt">
              <strong style={{ fontFamily: "var(--font-display)" }}>{st.step}</strong>
              <span style={{ display: "block", color: "var(--muted)", fontSize: 13.5 }}>{st.desc}</span>
            </span>
          </div>
        ))}
      </div>

      <BlockHead title="Conceitos" sub="Um termo por linha. Clique para ler a definição completa." />
      <ExpandTable
        minWidth={560}
        rowKey={(c) => c.termo}
        rows={CONCEITOS}
        columns={[
          { key: "termo", label: "Termo", render: (c) => <span className="name">{c.termo}</span> },
          { key: "resumo", label: "Resumo", render: (c) => <span className="sub" style={{ fontSize: 13 }}>{primeiraFrase(c.def)}</span> },
        ]}
        detail={(c) => <div className="detalhe" style={{ paddingTop: 8 }}>{c.def}</div>}
      />

      <BlockHead title="Versões e evolução" sub="Mais recente primeiro. Abra a linha para ver o que foi entregue." />
      <div className="alert-line warn">
        <span aria-hidden="true">!</span>
        <span>Regra: toda fase concluída registra aqui versão, data, entregas e impacto.</span>
      </div>
      <ExpandTable
        minWidth={560}
        rowKey={(v) => v.versao}
        rows={versoes}
        columns={[
          { key: "versao", label: "Versão", render: (v) => <span className="name" style={{ fontFamily: "var(--font-data)" }}>{v.versao}</span> },
          { key: "fase", label: "Fase", render: (v) => <Selo tone="info" dot={false}>{v.fase}</Selo> },
          { key: "n", label: "Entregas", align: "right", render: (v) => v.funcionalidades.length },
          { key: "data", label: "Data", align: "right", render: (v) => <span className="sub">{v.data}</span> },
        ]}
        detail={(v) => (
          <div className="detail-grid">
            <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4, fontSize: 13.5 }}>
              {v.funcionalidades.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
            <div className="detalhe"><span className="eyebrow">Impacto</span><div style={{ color: "var(--muted)" }}>{v.impacto}</div></div>
          </div>
        )}
      />
    </div>
  );
}
